import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Avatar,
  LinearProgress,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
} from '@mui/material';
import {
  HomeWorkOutlined,
  PeopleOutlined,
  ReceiptOutlined,
  MoreVertOutlined,
  DescriptionOutlined,
  CalendarTodayOutlined,
  NotificationsActiveOutlined,
  ArrowForwardOutlined,
  BoltOutlined,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { tritonStyles } from '../styles/TritonTheme';
import { useNavigate } from 'react-router-dom';

// Custom Components
import TodoDashboardWidget from './TodoDashboardWidget';
import FinanzKennzahlenDashboard from './FinanzKennzahlenDashboard';

// Styled Components
const DashboardContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: '#f7fafc',
  minHeight: 'calc(100vh - 72px)',
}));

const StatsCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #ffffff 0%, #f7fafc 100%)',
  border: '1px solid #e2e8f0',
  borderRadius: theme.spacing(2),
  transition: tritonStyles.transitions.standard,
  position: 'relative',
  overflow: 'visible',
  
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: tritonStyles.elevations.high,
  },
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    borderRadius: '12px 12px 0 0',
  },
}));

const MetricValue = styled(Typography)(({ theme, trend }) => ({
  fontSize: '2rem',
  fontWeight: 700,
  color: '#1a365d',
  marginBottom: theme.spacing(0.5),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  
  '& .trend-icon': {
    fontSize: '1.5rem',
    color: trend === 'up' ? '#38a169' : trend === 'down' ? '#e53e3e' : '#4a5568',
  },
}));

const ActivityCard = styled(Card)(({ theme }) => ({
  background: '#ffffff',
  border: '1px solid #e2e8f0',
  borderRadius: theme.spacing(2),
  boxShadow: tritonStyles.elevations.subtle,
}));

const QuickActionButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  padding: theme.spacing(2),
  background: 'linear-gradient(135deg, #ffffff 0%, #f7fafc 100%)',
  border: '2px solid #e2e8f0',
  color: '#2d3748',
  transition: tritonStyles.transitions.standard,
  height: '100%',
  
  '&:hover': {
    background: 'linear-gradient(135deg, #1a365d 0%, #2d3748 100%)',
    color: '#ffffff',
    borderColor: '#1a365d',
    transform: 'translateY(-2px)',
    boxShadow: tritonStyles.elevations.medium,
    
    '& .MuiSvgIcon-root': {
      color: '#ffffff',
    },
  },
  
  '& .MuiSvgIcon-root': {
    fontSize: '2rem',
    color: '#1a365d',
    marginBottom: theme.spacing(1),
  },
}));

// ToDo-Dashboard Daten (von API)
const initialTodoDashboard = { stats: { gesamt: 0, offen: 0, in_bearbeitung: 0, erledigt: 0, ueberfaellig: 0, diese_woche: 0, kritisch: 0, kuendigungen: 0, verhandlungen: 0 }, anstehende_todos: [] };

const TritonDashboard = () => {
  const navigate = useNavigate();
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [todoDashboard, setTodoDashboard] = useState(initialTodoDashboard);

  useEffect(() => {
    // Simuliere visuelles Ladeverhalten
    const timer = setInterval(() => {
      setLoadingProgress((oldProgress) => {
        if (oldProgress === 100) {
          clearInterval(timer);
          return 100;
        }
        const diff = Math.random() * 10;
        return Math.min(oldProgress + diff, 100);
      });
    }, 200);

    return () => {
      clearInterval(timer);
    };
  }, []);

  // ToDo-Daten laden
  useEffect(() => {
    const load = async () => {
      try {
        const data = await (await import('../services/api')).apiService.get('/api/vertrags-todos/dashboard');
        setTodoDashboard(data);
      } catch (e) {
        console.error('Fehler beim Laden der Dashboard-Daten:', e);
      }
    };
    load();
  }, []);

  const statsCards = useMemo(() => ([
    { title: 'Offene ToDos', value: todoDashboard.stats.offen, icon: ReceiptOutlined, color: '#3182ce' },
    { title: 'Überfällig', value: todoDashboard.stats.ueberfaellig, icon: NotificationsActiveOutlined, color: '#e53e3e' },
    { title: 'In Bearbeitung', value: todoDashboard.stats.in_bearbeitung, icon: PeopleOutlined, color: '#d69e2e' },
    { title: 'Diese Woche fällig', value: todoDashboard.stats.diese_woche, icon: CalendarTodayOutlined, color: '#38a169' },
  ]), [todoDashboard]);

  return (
    <DashboardContainer>
      {/* Willkommens-Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h1" sx={{ mb: 1 }}>
          Willkommen zurück
        </Typography>
        <Typography variant="body1" sx={{ color: '#4a5568', mb: 2 }}>
          Hier ist ein Überblick über Ihre Immobilienverwaltung
        </Typography>
        
        {/* Lade-Indikator */}
        <Box sx={{ mb: 2 }}>
          <LinearProgress 
            variant="determinate" 
            value={loadingProgress} 
            sx={{
              height: 4,
              borderRadius: 2,
              backgroundColor: '#e2e8f0',
              '& .MuiLinearProgress-bar': {
                background: tritonStyles.gradients.primary,
                borderRadius: 2,
              },
            }}
          />
        </Box>
      </Box>

      {/* Finanz-Kennzahlen */}
      <FinanzKennzahlenDashboard />

      {/* Statistiken */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statsCards.map((stat, index) => {
          const IconComponent = stat.icon;
          return (
            <Grid item xs={12} sm={6} lg={3} key={index}>
              <StatsCard sx={{ '&::before': { backgroundColor: stat.color } }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Avatar
                      sx={{
                        backgroundColor: `${stat.color}15`,
                        color: stat.color,
                        width: 48,
                        height: 48,
                      }}
                    >
                      <IconComponent />
                    </Avatar>
                    <IconButton size="small" sx={{ color: '#718096' }}>
                      <MoreVertOutlined fontSize="small" />
                    </IconButton>
                  </Box>
                  
                  <MetricValue>
                    {stat.value}
                  </MetricValue>
                  
                  <Typography variant="body2" sx={{ color: '#4a5568', mb: 1.5 }}>
                    {stat.title}
                  </Typography>
                </CardContent>
              </StatsCard>
            </Grid>
          );
        })}
      </Grid>

      <Grid container spacing={3}>
        {/* Anstehende ToDos als Aktivitäten */}
        <Grid item xs={12} lg={8}>
          <ActivityCard>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  Anstehende ToDos
                </Typography>
                <Button 
                  variant="text" 
                  endIcon={<ArrowForwardOutlined />}
                  sx={{ color: '#1a365d' }}
                  onClick={() => navigate('/dashboard/todo-management')}
                >
                  Zum ToDo-Board
                </Button>
              </Box>
              
              <List disablePadding>
                {(todoDashboard.anstehende_todos || []).map((todo, index) => (
                  <React.Fragment key={index}>
                    <ListItem sx={{ px: 0, py: 2 }}>
                      <ListItemAvatar>
                        <Avatar sx={{ backgroundColor: '#f7fafc', border: '1px solid #e2e8f0' }}>
                          <NotificationsActiveOutlined sx={{ color: '#d69e2e' }} />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="body1" sx={{ fontWeight: 500, color: '#2d3748' }}>
                            {todo.titel}
                          </Typography>
                        }
                        secondary={
                          <Box component="span" sx={{ display: 'block' }}>
                            <Box component="span" sx={{ display: 'block', color: '#4a5568', fontSize: '0.875rem' }}>
                              {todo.todo_typ} • {todo.anbieter || '—'}
                            </Box>
                            <Box component="span" sx={{ display: 'block', color: '#718096', fontSize: '0.75rem' }}>
                              Fällig in {typeof todo.tage_bis_faellig === 'number' ? `${todo.tage_bis_faellig} Tagen` : '—'}
                            </Box>
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < (todoDashboard.anstehende_todos?.length || 0) - 1 && (
                      <Divider sx={{ borderColor: '#e2e8f0' }} />
                    )}
                  </React.Fragment>
                ))}
              </List>
            </CardContent>
          </ActivityCard>
        </Grid>

        {/* ToDo-Management Widget - TEMPORARY DISABLED until DB tables exist */}
        {false && (
          <Grid item xs={12} lg={4}>
            <Box sx={{ mb: 3, height: 'fit-content' }}>
              <TodoDashboardWidget />
            </Box>
          </Grid>
        )}

        {/* Schnellaktionen */}
        <Grid item xs={12} lg={4}>
          <ActivityCard>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Schnellaktionen
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <QuickActionButton fullWidth onClick={() => navigate('/dashboard/buchung')}>
                    <Box sx={{ textAlign: 'center' }}>
                      <ReceiptOutlined />
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Neue Buchung
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        Buchung erstellen
                      </Typography>
                    </Box>
                  </QuickActionButton>
                </Grid>
                <Grid item xs={6}>
                  <QuickActionButton fullWidth onClick={() => navigate('/dashboard/objekte/neu')}>
                    <Box sx={{ textAlign: 'center' }}>
                      <HomeWorkOutlined />
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Objekt hinzufügen
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        Neues Objekt
                      </Typography>
                    </Box>
                  </QuickActionButton>
                </Grid>
                <Grid item xs={6}>
                  <QuickActionButton fullWidth onClick={() => navigate('/dashboard/belege')}>
                    <Box sx={{ textAlign: 'center' }}>
                      <DescriptionOutlined />
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Dokument upload
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        Datei hochladen
                      </Typography>
                    </Box>
                  </QuickActionButton>
                </Grid>
                <Grid item xs={6}>
                  <QuickActionButton fullWidth onClick={() => navigate('/dashboard/energie-dokument-inbox')}>
                    <Box sx={{ textAlign: 'center' }}>
                      <BoltOutlined />
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Energie-Inbox
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        Vertrag automatisch prüfen
                      </Typography>
                    </Box>
                  </QuickActionButton>
                </Grid>
                <Grid item xs={6}>
                  <QuickActionButton fullWidth onClick={() => navigate('/dashboard/todo-management?action=create')}>
                    <Box sx={{ textAlign: 'center' }}>
                      <CalendarTodayOutlined />
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Neues ToDo
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        Aufgabe anlegen
                      </Typography>
                    </Box>
                  </QuickActionButton>
                </Grid>
              </Grid>
            </CardContent>
          </ActivityCard>
        </Grid>
      </Grid>
    </DashboardContainer>
  );
};

export default TritonDashboard;
